import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MultiSelectItem } from '../../../shared/interfaces/multi-select.interface';

export interface MultiSelectDialogData {
  title: string;
  items: MultiSelectItem[];
  selectedItems: MultiSelectItem[];
  showAmounts: boolean;
}

@Component({
  selector: 'app-multi-select-dialog',
  templateUrl: './multi-select-dialog.component.html',
  styleUrls: ['./multi-select-dialog.component.css']
})
export class MultiSelectDialogComponent implements OnInit {
  searchControl = new FormControl('');
  filteredItems: MultiSelectItem[] = [];
  selectedMap: Map<string, MultiSelectItem> = new Map();

  constructor(
    public dialogRef: MatDialogRef<MultiSelectDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MultiSelectDialogData
  ) {}

  ngOnInit(): void {
    // Initialize selected map from passed selectedItems
    (this.data.selectedItems || []).forEach(item => {
      this.selectedMap.set(item.id, { ...item });
    });

    // Initialize filtered items
    this.filteredItems = [...this.data.items];

    // Listen for search changes
    this.searchControl.valueChanges.subscribe(value => {
      this.filterItems(value || '');
    });
  }

  private filterItems(searchTerm: string): void {
    const term = searchTerm.toLowerCase().trim();
    if (!term) {
      this.filteredItems = [...this.data.items];
    } else {
      this.filteredItems = this.data.items.filter(item =>
        item.name.toLowerCase().includes(term) ||
        (item.description && item.description.toLowerCase().includes(term))
      );
    }
  }

  isSelected(item: MultiSelectItem): boolean {
    return this.selectedMap.has(item.id);
  }

  toggleSelection(item: MultiSelectItem): void {
    if (this.selectedMap.has(item.id)) {
      this.selectedMap.delete(item.id);
    } else {
      this.selectedMap.set(item.id, { ...item, amount: item.amount || 1 });
    }
  }

  getAmount(item: MultiSelectItem): number {
    return this.selectedMap.get(item.id)?.amount || 0;
  }

  incrementAmount(item: MultiSelectItem, event: Event): void {
    event.stopPropagation();
    const existing = this.selectedMap.get(item.id);
    if (existing) {
      existing.amount = Math.min((existing.amount || 1) + 1, 99);
    } else {
      // Auto-select when incrementing from 0
      this.selectedMap.set(item.id, { ...item, amount: 1 });
    }
  }

  decrementAmount(item: MultiSelectItem, event: Event): void {
    event.stopPropagation();
    const existing = this.selectedMap.get(item.id);
    if (existing) {
      const newAmount = (existing.amount || 1) - 1;
      if (newAmount <= 0) {
        this.selectedMap.delete(item.id);
      } else {
        existing.amount = newAmount;
      }
    }
  }

  onAmountInput(item: MultiSelectItem, event: Event): void {
    event.stopPropagation();
    const input = event.target as HTMLInputElement;
    const val = Math.round(Number(input.value));
    if (val > 0) {
      const existing = this.selectedMap.get(item.id);
      if (existing) {
        existing.amount = Math.min(val, 99);
      } else {
        this.selectedMap.set(item.id, { ...item, amount: Math.min(val, 99) });
      }
    } else {
      this.selectedMap.delete(item.id);
    }
  }

  getSelectedCount(): number {
    return this.selectedMap.size;
  }

  onConfirm(): void {
    const result = Array.from(this.selectedMap.values());
    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}
